import { object, string } from 'yup';
import { validateYupSchema, yupToFormErrors } from 'formik';
import { TFunction } from 'i18next';

import { isValidNonNegativeNumber } from '../../validation/rules';
import { InvestingExperienceFormValues } from '../../models/Forms';
import { TableGroupResult } from '../../components/Form/Radio';

export enum InvestingExperienceOptions {
  LessThanTwoYears = 'lessThanTwoYears',
  MoreThanTwoYears = 'moreThanTwoYears',
  NoKnowledge = 'noKnowledge',
  NoPracticalExperience = 'noPracticalExperience',
}

export function makeInvestingExperienceSchema(t: TFunction) {
  function groupResultValidator(keys: string[]) {
    // Yup v1: the `.when()` callback receives the resolved match values as an
    // array (here `$groupResults` from the validation context) and the schema.
    return (values: any[], schema: any) => {
      const groupResults: TableGroupResult[] = values?.[0] ?? [];
      return groupResults.some((r) => keys.includes(r.question) && r.value !== InvestingExperienceOptions.NoKnowledge)
        ? schema.required(t('form.required'))
        : schema.notRequired();
    };
  }

  function experienceNumericField(keys: string[]) {
    return string()
      .when('$groupResults', groupResultValidator(keys))
      .test('nonNegativeNumber', t('form.nonNegativeNumberInvalid'), (value) => {
        if (value === undefined || value === '') {
          return true;
        }
        return isValidNonNegativeNumber(value);
      });
  }

  return object().shape({
    a: string().when('$groupResults', groupResultValidator(['a'])),
    b: object()
      .default(null)
      .nullable()
      .when('$groupResults', groupResultValidator(['b', 'c']))
      .shape({
        a: string().when('$groupResults', groupResultValidator(['b', 'c'])),
        b: string().when('$groupResults', groupResultValidator(['b', 'c'])),
        c: experienceNumericField(['b', 'c']),
        d: experienceNumericField(['b', 'c']),
      }),
    c: object()
      .default(null)
      .nullable()
      .when('$groupResults', groupResultValidator(['c', 'd']))
      .shape({
        a: string().when('$groupResults', groupResultValidator(['c', 'd'])),
        b: string().when('$groupResults', groupResultValidator(['c', 'd'])),
        c: experienceNumericField(['c', 'd']),
        d: experienceNumericField(['c', 'd']),
      }),
    useOfServices: string().required(t('form.required')),
    wayOfEducation: string().required(t('form.required')),
    stockTradingLoan: string().required(t('form.required')),
  });
}

export function validateInvestingExperience(
  values: InvestingExperienceFormValues,
  groupResults: TableGroupResult[],
  t: TFunction,
) {
  const schema = makeInvestingExperienceSchema(t);

  try {
    validateYupSchema<InvestingExperienceFormValues>(values, schema, true, { groupResults });
  } catch (error) {
    return yupToFormErrors(error);
  }

  return {};
}
